import { Routes } from '@angular/router';
import {
  LoginFormComponent,
  ResetPasswordFormComponent,
  CreateAccountFormComponent,
  ChangePasswordFormComponent,
} from './components';
import { AuthGuardService } from './services';
import { AdminGuardService } from './services/admin-guard.service';
import { SideNavOuterToolbarComponent, UnauthenticatedContentComponent } from './layouts';

// Las pantallas internas del CRM se cargan lazy via `loadComponent`. Esto reduce
// el bundle inicial (de ~9.5 MB a ~6 MB esperado) y emite un chunk JS por feature.
// Solo quedan eager los layouts/auth que son criticos para el shell inicial.

export const routes: Routes = [
  {
    path: 'auth',
    component: UnauthenticatedContentComponent,
    children: [
      {
        path: 'sign-in',
        component: LoginFormComponent,
        canActivate: [AuthGuardService],
      },
      {
        path: 'reset-password',
        component: ResetPasswordFormComponent,
        canActivate: [AuthGuardService],
      },
      {
        path: 'register',
        component: CreateAccountFormComponent,
        canActivate: [AuthGuardService],
      },
      {
        path: 'change-password/:recoveryCode',
        component: ChangePasswordFormComponent,
        canActivate: [AuthGuardService],
      },
      ...[
        { from: 'create-account', to: 'register' },
        { from: 'login', to: 'sign-in' },
      ].map(redirect => ({ path: redirect.from, redirectTo: redirect.to, pathMatch: 'full' as const })),
      {
        path: '**',
        redirectTo: 'sign-in',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '',
    component: SideNavOuterToolbarComponent,
    canActivate: [AuthGuardService],
    children: [
      {
        path: 'home',
        // Home admin/cliente — render condicional segun rol del usuario.
        loadComponent: () => import('./pages/home/home.component')
          .then(m => m.HomeComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'chat',
        loadComponent: () => import('./pages/chat/chat.component')
          .then(m => m.ChatComponent),
        canActivate: [AuthGuardService],
      },
      {
        // Demo aislada de propuestas de diseno (Lucide, empty states, skeletons,
        // transiciones, spacing aereo). No toca produccion — solo visualizacion.
        path: 'design-preview',
        loadComponent: () => import('./pages/design-preview/design-preview.component')
          .then(m => m.DesignPreviewComponent),
        canActivate: [AuthGuardService],
      },
      {
        // Mock interactivo de propuesta de sidebar moderno (Lucide, magic indicator,
        // command palette Cmd+K, workspace switcher, pinned/recientes, flyout en rail).
        // No toca el sidebar real — todo el markup es ad-hoc en la pagina.
        path: 'design-preview-sidebar',
        loadComponent: () => import('./pages/design-preview-sidebar/design-preview-sidebar.component')
          .then(m => m.DesignPreviewSidebarComponent),
        canActivate: [AuthGuardService],
      },
      {
        // Mock interactivo del toolbar moderno — breadcrumb, back/forward,
        // command palette, page actions slot, scroll-aware, online dot.
        path: 'design-preview-toolbar',
        loadComponent: () => import('./pages/design-preview-toolbar/design-preview-toolbar.component')
          .then(m => m.DesignPreviewToolbarComponent),
        canActivate: [AuthGuardService],
      },
      {
        // Vitrina aislada de la grilla moderna 2026 — vitrina de R1-R6.
        // Cards mockeadas con presence + comments + AI overlay + sparkline +
        // summary bar + view modes. Toggle desktop/mobile manual.
        path: 'design-preview-grid',
        loadComponent: () => import('./pages/design-preview-grid/design-preview-grid.component')
          .then(m => m.DesignPreviewGridComponent),
        canActivate: [AuthGuardService],
      },
      {
        // Changelog del sistema — restringido a admins (claim `cliente:viewall`).
        path: 'changelog',
        loadComponent: () => import('./pages/changelog/changelog.component')
          .then(m => m.ChangelogComponent),
        canActivate: [AuthGuardService, AdminGuardService],
      },
      {
        path: 'usuarios',
        loadComponent: () => import('./pages/usuarios-list/usuarios-list.component')
          .then(m => m.UsuariosListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'asignacion-perfil',
        loadComponent: () => import('./pages/asignacion-perfil/asignacion-perfil.component')
          .then(m => m.AsignacionPerfilComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'parametros',
        loadComponent: () => import('./pages/parametros-list/parametros-list.component')
          .then(m => m.ParametrosListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'parametros/:id',
        loadComponent: () => import('./pages/parametro-detail/parametro-detail.component')
          .then(m => m.ParametroDetailComponent),
        canActivate: [AuthGuardService],
      },
      // ===== Catalogos: TiposAcontecimiento + 3 NoConformidades =====
      {
        path: 'tipos-acontecimiento',
        loadComponent: () => import('./pages/tipos-acontecimiento-list/tipos-acontecimiento-list.component')
          .then(m => m.TiposAcontecimientoListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'tipos-acontecimiento/:id',
        loadComponent: () => import('./pages/tipo-acontecimiento-detail/tipo-acontecimiento-detail.component')
          .then(m => m.TipoAcontecimientoDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'origenes-no-conformidad',
        loadComponent: () => import('./pages/origenes-no-conformidad-list/origenes-no-conformidad-list.component')
          .then(m => m.OrigenesNoConformidadListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'origenes-no-conformidad/:id',
        loadComponent: () => import('./pages/origen-no-conformidad-detail/origen-no-conformidad-detail.component')
          .then(m => m.OrigenNoConformidadDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'tipos-no-conformidad',
        loadComponent: () => import('./pages/tipos-no-conformidad-list/tipos-no-conformidad-list.component')
          .then(m => m.TiposNoConformidadListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'tipos-no-conformidad/:id',
        loadComponent: () => import('./pages/tipo-no-conformidad-detail/tipo-no-conformidad-detail.component')
          .then(m => m.TipoNoConformidadDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'acciones-no-conformidad',
        loadComponent: () => import('./pages/acciones-no-conformidad-list/acciones-no-conformidad-list.component')
          .then(m => m.AccionesNoConformidadListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'acciones-no-conformidad/:id',
        loadComponent: () => import('./pages/accion-no-conformidad-detail/accion-no-conformidad-detail.component')
          .then(m => m.AccionNoConformidadDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'asignacion-perfil/:usuarioId',
        loadComponent: () => import('./pages/asignacion-perfil/asignacion-perfil.component')
          .then(m => m.AsignacionPerfilComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'roles',
        loadComponent: () => import('./pages/roles-list/roles-list.component')
          .then(m => m.RolesListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'clientes',
        loadComponent: () => import('./pages/clientes-list/clientes-list.component')
          .then(m => m.ClientesListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'clientes/:id',
        loadComponent: () => import('./pages/cliente-detail/cliente-detail.component')
          .then(m => m.ClienteDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'contactos',
        loadComponent: () => import('./pages/contactos-list/contactos-list.component')
          .then(m => m.ContactosListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'contactos/:id',
        loadComponent: () => import('./pages/contacto-detail/contacto-detail.component')
          .then(m => m.ContactoDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'industrias',
        loadComponent: () => import('./pages/industrias-list/industrias-list.component')
          .then(m => m.IndustriasListComponent),
        canActivate: [AuthGuardService],
      },
      {
        // Vista unificada: tabs IONICS / Espera / Planta. Espera ?tab= para deep-link.
        path: 'personal',
        loadComponent: () => import('./pages/personal-list/personal-list.component')
          .then(m => m.PersonalListComponent),
        canActivate: [AuthGuardService],
      },
      // Backwards-compat: redirige las rutas viejas al nuevo path con el tab correspondiente.
      { path: 'empleados-ionics',  redirectTo: 'personal?tab=ionics', pathMatch: 'full' },
      { path: 'personas-en-planta', redirectTo: 'personal?tab=planta', pathMatch: 'full' },
      { path: 'personas-en-espera', redirectTo: 'personal?tab=espera', pathMatch: 'full' },
      {
        path: 'empleados-en-planta',
        loadComponent: () => import('./pages/empleados-en-planta-list/empleados-en-planta-list.component')
          .then(m => m.EmpleadosEnPlantaListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'productos',
        loadComponent: () => import('./pages/productos-list/productos-list.component')
          .then(m => m.ProductosListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'productos/:id',
        loadComponent: () => import('./pages/producto-detail/producto-detail.component')
          .then(m => m.ProductoDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'pedidos',
        loadComponent: () => import('./pages/pedidos-list/pedidos-list.component')
          .then(m => m.PedidosListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'pedidos/nuevo',
        loadComponent: () => import('./pages/pedido-form/pedido-form.component')
          .then(m => m.PedidoFormComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'pedidos/:id/editar',
        loadComponent: () => import('./pages/pedido-form/pedido-form.component')
          .then(m => m.PedidoFormComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'pedidos/:id',
        loadComponent: () => import('./pages/pedido-detail/pedido-detail.component')
          .then(m => m.PedidoDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'eventualidades',
        loadComponent: () => import('./pages/eventualidades-list/eventualidades-list.component')
          .then(m => m.EventualidadesListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'eventualidades/:id',
        loadComponent: () => import('./pages/eventualidad-detail/eventualidad-detail.component')
          .then(m => m.EventualidadDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'tipos-eventualidad',
        loadComponent: () => import('./pages/tipos-eventualidad-list/tipos-eventualidad-list.component')
          .then(m => m.TiposEventualidadListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'tipos-eventualidad/:id',
        loadComponent: () => import('./pages/tipo-eventualidad-detail/tipo-eventualidad-detail.component')
          .then(m => m.TipoEventualidadDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'sub-tipos-eventualidad',
        loadComponent: () => import('./pages/sub-tipos-eventualidad-list/sub-tipos-eventualidad-list.component')
          .then(m => m.SubTiposEventualidadListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'sub-tipos-eventualidad/:id',
        loadComponent: () => import('./pages/sub-tipo-eventualidad-detail/sub-tipo-eventualidad-detail.component')
          .then(m => m.SubTipoEventualidadDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'no-conformidades',
        loadComponent: () => import('./pages/no-conformidades-list/no-conformidades-list.component')
          .then(m => m.NoConformidadesListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'no-conformidades/:id',
        loadComponent: () => import('./pages/no-conformidad-detail/no-conformidad-detail.component')
          .then(m => m.NoConformidadDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'novedades',
        loadComponent: () => import('./pages/novedades-list/novedades-list.component')
          .then(m => m.NovedadesListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'novedades/:id',
        loadComponent: () => import('./pages/novedad-detail/novedad-detail.component')
          .then(m => m.NovedadDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'tipos-novedad',
        loadComponent: () => import('./pages/tipos-novedad-list/tipos-novedad-list.component')
          .then(m => m.TiposNovedadListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'tipos-novedad/:id',
        loadComponent: () => import('./pages/tipo-novedad-detail/tipo-novedad-detail.component')
          .then(m => m.TipoNovedadDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'mails',
        loadComponent: () => import('./pages/mails-list/mails-list.component')
          .then(m => m.MailsListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'acontecimientos',
        loadComponent: () => import('./pages/acontecimientos-list/acontecimientos-list.component')
          .then(m => m.AcontecimientosListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'acontecimientos/:id',
        loadComponent: () => import('./pages/acontecimiento-detail/acontecimiento-detail.component')
          .then(m => m.AcontecimientoDetailComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'cronograma',
        loadComponent: () => import('./pages/cronograma/cronograma.component')
          .then(m => m.CronogramaComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'control-actividad',
        loadComponent: () => import('./pages/control-actividad/control-actividad.component')
          .then(m => m.ControlActividadComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'secuenciamiento',
        loadComponent: () => import('./pages/secuenciamiento/secuenciamiento.component')
          .then(m => m.SecuenciamientoComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'cubo-test',
        loadComponent: () => import('./pages/cubo-test/cubo-test.component')
          .then(m => m.CuboTestComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'biblioteca',
        loadComponent: () => import('./pages/biblioteca/biblioteca.component')
          .then(m => m.BibliotecaComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'crm-contact-list',
        loadComponent: () => import('./pages/crm-contact-list/crm-contact-list.component')
          .then(m => m.CrmContactListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'crm-contact-details',
        loadComponent: () => import('./pages/crm-contact-details/crm-contact-details.component')
          .then(m => m.CrmContactDetailsComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'planning-task-list',
        loadComponent: () => import('./pages/planning-task-list/planning-task-list.component')
          .then(m => m.PlanningTaskListComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'planning-task-details',
        loadComponent: () => import('./pages/planning-task-details/planning-task-details.component')
          .then(m => m.PlanningTaskDetailsComponent),
      },
      {
        path: 'planning-scheduler',
        loadComponent: () => import('./pages/planning-scheduler/planning-scheduler.component')
          .then(m => m.PlanningSchedulerComponent),
      },
      {
        path: 'analytics-dashboard',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'analytics-sales-analysis',
        loadComponent: () => import('./pages/analytics-sales-analysis/analytics-sales-analysis.component')
          .then(m => m.AnalyticsSalesAnalysisComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'analytics-geography',
        loadComponent: () => import('./pages/analytics-geography/analytics-geography.component')
          .then(m => m.AnalyticsGeographyComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'sign-in-form',
        loadComponent: () => import('./pages/sign-in-form/sign-in-form.component')
          .then(m => m.AppSignInComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'register-form',
        loadComponent: () => import('./pages/register-form/register-form.component')
          .then(m => m.AppRegisterComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'reset-password-form',
        loadComponent: () => import('./pages/reset-password-form/reset-password-form.component')
          .then(m => m.AppResetPasswordComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'user-profile',
        loadComponent: () => import('./pages/user-profile/user-profile.component')
          .then(m => m.UserProfileComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'legacy/:controller',
        loadComponent: () => import('./pages/legacy-placeholder/legacy-placeholder.component')
          .then(m => m.LegacyPlaceholderComponent),
        canActivate: [AuthGuardService],
      },
      ...[
        { from: 'analytics-sales-report', to: 'analytics-sales-analysis' },
        { from: 'sign-up-form', to: 'register-form' },
      ].map(redirect => ({ path: redirect.from, redirectTo: redirect.to, pathMatch: 'full' as const })),
      {
        path: '**',
        redirectTo: 'home',
        pathMatch: 'full',
      },
    ],
  },
];
